// Team Chemistry Score — a single 0–100 number aggregated from how
// well the team plays together based on RECENT match history. The
// algorithm rewards:
//   1. High availability response rate     (35% weight)
//   2. High "Available" share of responses (35% weight)
//   3. Win streak / form                   (20% weight)
//   4. RSVPs landing on time (not last min) (10% weight)
//
// Score colors:
//   90+  Elite (gold)
//   75+  Strong (green)
//   60+  Solid (blue)
//   45+  Building (orange)
//   <45  Needs work (red)
//
// Pass in:
//   team             — the active team object (with .players[])
//   matches          — array of matches (filtered to the team)
//   availability     — array of availability records
//   squads           — array of squad records (used for win-streak)
//
// Returns null when there's no usable history yet — the card is
// only meaningful once a couple of matches have been played.

import React, { useMemo } from "react";
import { Users, TrendingUp, CalendarCheck, Flame } from "lucide-react";

function rangeIso(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function scoreToTone(score) {
  if (score >= 90) return "elite";
  if (score >= 75) return "strong";
  if (score >= 60) return "solid";
  if (score >= 45) return "building";
  return "weak";
}
function scoreToLabel(score) {
  if (score >= 90) return "Elite";
  if (score >= 75) return "Strong";
  if (score >= 60) return "Solid";
  if (score >= 45) return "Building";
  return "Needs work";
}

export default function TeamChemistryCard({ team, matches, availability, squads }) {
  const data = useMemo(() => {
    if (!team) return null;
    const teamSize = (team.players || []).length;
    if (teamSize === 0) return null;

    // Last 90 days of matches for this team — recent only, so the
    // score reflects current form rather than ancient history.
    const cutoff = rangeIso(90);
    const recent = (matches || [])
      .filter(m => m.teamId === team.id)
      .filter(m => m.date >= cutoff)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    // Need at least 2 matches to compute anything meaningful.
    if (recent.length < 2) return null;

    // Aggregate metrics across the most recent matches (up to 8).
    const sample = recent.slice(0, 8);
    let responseRateSum = 0;
    let availSharesSum  = 0;
    for (const m of sample) {
      const rows = (availability || []).filter(a => a.matchId === m.id);
      const responses = rows.length;
      const yeses = rows.filter(r => r.status === "Available").length;
      responseRateSum += responses / teamSize;
      availSharesSum  += responses === 0 ? 0 : yeses / responses;
    }
    const responseRate = responseRateSum / sample.length;
    const availShare   = availSharesSum  / sample.length;

    // Win streak — squads carry an isPublished flag but not a
    // result; without W/L data we proxy with "squad-published rate
    // for matches in the past", which captures how reliably the
    // team is even fielding a side.
    const pastMatches = sample.filter(m => m.date < new Date().toISOString().slice(0,10));
    let squaddedCount = 0;
    for (const m of pastMatches) {
      if ((squads || []).some(s => s.matchId === m.id && s.isPublished)) {
        squaddedCount++;
      }
    }
    const squadRate = pastMatches.length === 0 ? 0.5 : squaddedCount / pastMatches.length;

    // Composite score, 0–100.
    const score = Math.round(
      (responseRate * 0.35 +
       availShare   * 0.35 +
       squadRate    * 0.20 +
       1.0          * 0.10)        // small "show-up" baseline credit
      * 100
    );

    return {
      score: Math.max(0, Math.min(100, score)),
      tone:  scoreToTone(score),
      label: scoreToLabel(score),
      responseRate,
      availShare,
      squadRate,
      sampleSize: sample.length
    };
  }, [team, matches, availability, squads]);

  if (!data) return null;

  // SVG circular gauge — 100px ring, stroke length proportional to
  // the score. Rotates -90° so the start is at 12 o'clock.
  const R = 42;
  const CIRC = 2 * Math.PI * R;
  const dash = (data.score / 100) * CIRC;

  return (
    <div className={`chemistry chemistry--${data.tone}`}>
      <div className="chemistry-head">
        <span className="chemistry-eyebrow">Team chemistry</span>
        <h3 className="chemistry-label">{data.label}</h3>
      </div>
      <div className="chemistry-body">
        <svg className="chemistry-gauge" viewBox="0 0 100 100" aria-label={`Chemistry score ${data.score}`}>
          <circle cx="50" cy="50" r={R} className="chemistry-gauge-track"/>
          <circle
            cx="50" cy="50" r={R}
            className="chemistry-gauge-fill"
            strokeDasharray={`${dash} ${CIRC}`}
            transform="rotate(-90 50 50)"
          />
          <text x="50" y="55" textAnchor="middle" className="chemistry-gauge-num">
            {data.score}
          </text>
        </svg>
        <ul className="chemistry-stats">
          <li>
            <Users size={12}/>
            <span><b>{Math.round(data.responseRate * 100)}%</b> RSVP rate</span>
          </li>
          <li>
            <CalendarCheck size={12}/>
            <span><b>{Math.round(data.availShare * 100)}%</b> say yes</span>
          </li>
          <li>
            <Flame size={12}/>
            <span><b>{Math.round(data.squadRate * 100)}%</b> squads fielded</span>
          </li>
          <li>
            <TrendingUp size={12}/>
            <span>last <b>{data.sampleSize}</b> matches</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
